/* GENERATED by scripts/build-assets.sh — do not edit.
   Sources, in order: tittli-header.js tittli-whatsapp.js tittli-wishlist.js tittli-announcement-bar.js tittli-slider.js tittli-quick-add.js */

/* ===================== tittli-header.js ===================== */
/*
 * <tittli-menu-drawer> — the header's slide-in main menu.
 *
 * Built on a native modal <dialog>, so the browser owns the focus trap, the
 * inert background, the backdrop and Escape-to-close. What is left for this
 * module is the part the platform does not guarantee: returning focus to the
 * control that opened the drawer, and closing when the backdrop is clicked.
 *
 * Openers live in the header markup rather than inside the element, so the
 * click handler is delegated once at the document instead of bound per button.
 */

const OPEN_ATTRIBUTE = "data-drawer-open";
const CLOSE_ATTRIBUTE = "data-drawer-close";

class TittliDrawer extends HTMLElement {
  #dialog = null;
  #invoker = null;

  connectedCallback() {
    this.#dialog = this.querySelector("dialog");

    if (!this.#dialog) {
      return;
    }

    this.#dialog.addEventListener("click", this.#onDialogClick);
    this.#dialog.addEventListener("close", this.#onClose);
  }

  disconnectedCallback() {
    this.#dialog?.removeEventListener("click", this.#onDialogClick);
    this.#dialog?.removeEventListener("close", this.#onClose);
  }

  open(invoker) {
    if (!this.#dialog || this.#dialog.open) {
      return;
    }

    this.#invoker = invoker ?? null;
    this.#dialog.showModal();
  }

  close() {
    this.#dialog?.close();
  }

  /*
   * A modal <dialog> stretches across the viewport, so clicks that land on the
   * dialog itself — rather than on the panel inside it — are backdrop clicks.
   */
  #onDialogClick = (event) => {
    if (event.target === this.#dialog) {
      this.close();
      return;
    }

    if (event.target.closest?.(`[${CLOSE_ATTRIBUTE}]`)) {
      this.close();
    }
  };

  /*
   * Fires for every close path, including Escape and form submission, which is
   * why focus restoration is hung off the event rather than off close().
   */
  #onClose = () => {
    this.#invoker?.focus();
    this.#invoker = null;
  };
}

if (!customElements.get("tittli-menu-drawer")) {
  customElements.define("tittli-menu-drawer", TittliDrawer);
}

document.addEventListener("click", (event) => {
  const trigger = event.target.closest?.(`[${OPEN_ATTRIBUTE}]`);

  if (!trigger) {
    return;
  }

  const dialog = document.getElementById(trigger.getAttribute(OPEN_ATTRIBUTE));
  const drawer = dialog?.closest("tittli-menu-drawer");

  if (drawer instanceof TittliDrawer) {
    event.preventDefault();
    drawer.open(trigger);
  }
});

/*
 * The announcement bar's measured height, published as
 * --tittli-announcement-height.
 *
 * The header pins to the underside of the bar, so its sticky offset has to be
 * what the bar actually occupies — and that is not a constant. The message
 * wraps to two lines on a narrow screen, the merchant can write a longer one,
 * and a font loading late can change it after first paint. The stylesheet's
 * 48px is the right answer for a single line and the wrong one otherwise, so
 * it is treated as the fallback and corrected from the rendered box.
 *
 * Observed rather than measured once: wrapping changes with the viewport, and
 * a resize listener would miss a change the bar makes on its own.
 */
{
  const bar = document.querySelector('.section-announcement');

  if (bar && 'ResizeObserver' in window) {
    const publish = (height) => {
      if (height > 0) {
        document.documentElement.style.setProperty('--tittli-announcement-height', `${Math.round(height)}px`);
      }
    };

    new ResizeObserver(([entry]) => publish(entry.target.getBoundingClientRect().height)).observe(bar);
    publish(bar.getBoundingClientRect().height);
  }
}

/* ===================== tittli-whatsapp.js ===================== */
/*
 * <tittli-whatsapp> — the floating WhatsApp button and its card.
 *
 * The card is markup, not something this builds: with the module absent the
 * button is still a button and the card simply never opens, rather than the
 * shop showing a control that does nothing.
 *
 * What the element adds is the opening and closing, and the three ways out a
 * floating panel needs: the button itself, Escape, and a click anywhere else
 * on the page.
 */

class TittliWhatsApp extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.tittli-wa__panel');
    this.button = this.querySelector('[data-wa-toggle]');
    if (!this.panel || !this.button) return;

    this.button.addEventListener('click', () => this.toggle());

    /* Escape closes, which is what a dialog-shaped thing is expected to do,
     * and returns focus to the control that opened it. */
    this.onKeydown = (event) => {
      if (event.key !== 'Escape' || !this.open) return;
      this.toggle(false);
      this.button.focus();
    };
    document.addEventListener('keydown', this.onKeydown);

    /* A click outside closes it. Checked against the element rather than the
     * panel so the button's own click is not counted twice. */
    this.onDocumentClick = (event) => {
      if (!this.open || this.contains(event.target)) return;
      this.toggle(false);
    };
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
  }

  get open() {
    return this.classList.contains('is-open');
  }

  toggle(next = !this.open) {
    this.classList.toggle('is-open', next);
    this.panel.hidden = !next;
    this.button.setAttribute('aria-expanded', String(next));

    const label = this.querySelector('[data-wa-label]');
    const text = next ? this.button.dataset.labelClose : this.button.dataset.labelOpen;
    if (label && text) label.textContent = text;

    /* Focus the way out, so a keyboard lands inside the card it just opened
     * rather than carrying on down the page behind it. */
    if (next) this.querySelector('.tittli-wa__start')?.focus();
  }
}

if (!customElements.get('tittli-whatsapp')) {
  customElements.define('tittli-whatsapp', TittliWhatsApp);
}

/* ===================== tittli-wishlist.js ===================== */
/*
 * The wishlist.
 *
 * Saved in localStorage, because a Shopify theme has nowhere else to put it:
 * there is no customer field a theme can write to without an app and an app
 * proxy. That has consequences worth knowing — the list lives on the device
 * that made it, does not follow the shopper to their phone, and goes when they
 * clear site data. It is a "keep this for later while I shop", not an account
 * feature.
 *
 * Handles are what is stored, not variant ids or titles: a handle is the one
 * thing that still resolves after a price change, a rename or a restock, and
 * it is enough to ask the shop for the rest.
 */

const KEY = 'tittli-wishlist';
const EVENT = 'tittli:wishlist';
const VIEW_KEY = 'tittli-wishlist-view';
const VIEWS = ['grid', 'list'];

const read = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY));
    return Array.isArray(raw) ? raw.filter((h) => typeof h === 'string') : [];
  } catch {
    /* Private mode, blocked storage, or something else wrote nonsense to the
     * key. An empty list is wrong but harmless; throwing here would take the
     * page's other scripts down with it. */
    return [];
  }
};

const write = (list) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* Out of quota or storage denied. The page keeps working; the list simply
     * does not outlive it. */
  }
  document.dispatchEvent(new CustomEvent(EVENT, { detail: { list } }));
};

const Wishlist = {
  all: read,
  has: (handle) => read().includes(handle),
  toggle(handle) {
    const list = read();
    const at = list.indexOf(handle);
    if (at === -1) list.unshift(handle);
    else list.splice(at, 1);
    write(list);
    return at === -1;
  },
  remove(handle) {
    write(read().filter((h) => h !== handle));
  },
};

window.TittliWishlist = Wishlist;

/* ---------- grid or list ---------- */

/*
 * Which of the two layouts the shopper last chose. Kept beside the list and
 * read the same guarded way: storage can be unavailable, and a layout is not
 * worth an exception.
 */
const readView = () => {
  try {
    const view = localStorage.getItem(VIEW_KEY);
    return VIEWS.includes(view) ? view : 'grid';
  } catch {
    return 'grid';
  }
};

const writeView = (view) => {
  try {
    localStorage.setItem(VIEW_KEY, view);
  } catch {
    /* The choice holds for this page and no longer. */
  }
};

/* ---------- the heart ---------- */

class TittliWish extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.handle = this.dataset.handle;
    if (!this.button || !this.handle) return;

    this.sync();
    this.button.addEventListener('click', this.onClick);
    document.addEventListener(EVENT, this.sync);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    document.removeEventListener(EVENT, this.sync);
  }

  /* On a product card the heart sits inside the link to the product, so a tap
   * on it would otherwise follow that link. */
  onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    Wishlist.toggle(this.handle);
  };

  sync = () => {
    const saved = Wishlist.has(this.handle);
    this.classList.toggle('is-saved', saved);
    this.button.setAttribute('aria-pressed', String(saved));

    const label = this.button.querySelector('[data-wish-label]');
    const next = saved ? this.dataset.labelSaved : this.dataset.labelSave;
    if (label && next) label.textContent = next;
  };
}

/* ---------- the count beside the header's heart ---------- */

const paintCounts = () => {
  const count = Wishlist.all().length;
  document.querySelectorAll('[data-wishlist-count]').forEach((node) => {
    node.textContent = count;
    node.hidden = count === 0;
  });
  /* The link itself, not only its badge: on a phone the header hides it while
   * the list is empty. A heart that leads to "nothing saved yet" is a control
   * that can only disappoint, and the row it sits in has three other things
   * in it that a phone has to find room for. */
  document.querySelectorAll('[data-wishlist-link]').forEach((node) => {
    node.classList.toggle('is-empty', count === 0);
  });
};

/* ---------- the list itself ---------- */

/*
 * Each saved product is asked for as its own card, through an alternate
 * product template that renders the theme's card and nothing else. The
 * alternative was rebuilding the card in JavaScript from /products/x.js, which
 * would be a second card to keep in step with the first for ever.
 */
class TittliWishlistGrid extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.total = this.querySelector('[data-wishlist-total]');
    this.views = this.querySelector('[data-wishlist-views]');
    this.viewButtons = [...this.querySelectorAll('[data-wishlist-view]')];
    if (!this.grid) return;

    this.setView(readView());
    this.viewButtons.forEach((button) => button.addEventListener('click', this.onViewClick));

    this.render();
    document.addEventListener(EVENT, this.onChange);
  }

  disconnectedCallback() {
    this.viewButtons?.forEach((button) => button.removeEventListener('click', this.onViewClick));
    document.removeEventListener(EVENT, this.onChange);
  }

  onViewClick = (event) => {
    this.setView(event.currentTarget.dataset.wishlistView);
  };

  /* The class goes on the grid and the CSS does the rest — the cards
   * themselves are the same markup in either view, so nothing is re-fetched
   * and the scroll position does not move under the shopper. */
  setView(view) {
    const next = VIEWS.includes(view) ? view : 'grid';
    this.grid.classList.toggle('tittli-wishlist__grid--list', next === 'list');
    this.viewButtons.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.wishlistView === next));
    });
    writeView(next);
  }

  /* Only a removal needs a re-render here, and only of the card removed —
   * refetching the rest would throw away markup that has not changed. */
  onChange = () => {
    const saved = new Set(Wishlist.all());
    let removed = false;
    this.grid.querySelectorAll('[data-wishlist-item]').forEach((item) => {
      if (saved.has(item.dataset.wishlistItem)) return;
      item.remove();
      removed = true;
    });
    if (removed) this.toggleEmpty();
  };

  /* `count` is passed while the cards are still being fetched, when the grid
   * is empty but the list is not — without it the page would show "nothing
   * saved yet" for as long as the round trip takes, to a shopper who has
   * saved things. */
  toggleEmpty(count = this.grid.children.length) {
    const has = count > 0;
    this.grid.hidden = !has;
    if (this.empty) this.empty.hidden = has;
    /* A layout switch over an empty page is a control with nothing to act on. */
    if (this.views) this.views.hidden = !has;

    if (!this.total) return;
    const { countOne, countOther } = this.total.dataset;
    this.total.textContent = count === 1 ? countOne : (countOther || '').replace('[n]', count);
    this.total.hidden = !has;
  }

  async render() {
    const handles = Wishlist.all();
    this.toggleEmpty(handles.length);
    if (!handles.length) return;

    const cards = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${encodeURIComponent(handle)}?view=wishlist-card`);
          if (!response.ok) return null;
          return { handle, html: await response.text() };
        } catch {
          return null;
        }
      }),
    );

    /* A product that has been deleted or unpublished answers 404. It is
     * dropped from the list as well as from the page, so the shopper is not
     * carrying a saved item that no longer exists. */
    cards.forEach((card, index) => {
      if (card) return;
      Wishlist.remove(handles[index]);
    });

    this.grid.replaceChildren(
      ...cards.filter(Boolean).map(({ handle, html }) => {
        const item = document.createElement('li');
        item.className = 'tittli-wishlist__item';
        item.dataset.wishlistItem = handle;
        item.innerHTML = html;
        return item;
      }),
    );

    this.toggleEmpty();
  }
}

if (!customElements.get('tittli-wish')) customElements.define('tittli-wish', TittliWish);
if (!customElements.get('tittli-wishlist')) customElements.define('tittli-wishlist', TittliWishlistGrid);

document.addEventListener(EVENT, paintCounts);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', paintCounts);
else paintCounts();

/* ===================== tittli-announcement-bar.js ===================== */
/*
 * <tittli-ticker> — cross-fades the announcement bar's messages.
 *
 * The bar renders and reads correctly with this module absent: CSS keeps the
 * first message visible and hides the rest. Upgrading swaps that for a stacked
 * cross-fade, which is why the "is-ready" class only lands once there is more
 * than one message to rotate through.
 *
 * The rotation itself is a CSS animation — the main thread does no per-frame
 * work. Only the keyframes have to be generated here, because their stop
 * positions depend on how many messages the merchant added, and CSS cannot
 * compute keyframe percentages from a custom property.
 */

/* One constructed stylesheet holds every generated keyframe set, built at most
 * once per distinct message count no matter how many bars are on the page. */
const keyframeCounts = new Set();
let keyframeSheet = null;

function keyframeName(count) {
  return `tittli-ticker-${count}`;
}

/*
 * Each message animates on the same timeline and is offset by a positive
 * delay, so message *i* is opaque for the *i*-th slot of the cycle and
 * transparent for the rest of it. `fade` is capped so short intervals do not
 * turn the whole slot into a transition.
 *
 * The travel is sideways: a message arrives from the right and leaves to the
 * left, so a run of them reads as a queue moving one way rather than as text
 * nudging up and down in place. It is still one message at a time — the shift
 * is a few pixels of direction, not a march across the bar.
 */
function ensureKeyframes(count) {
  if (keyframeCounts.has(count)) {
    return true;
  }

  if (!keyframeSheet) {
    if (typeof CSSStyleSheet !== "function" || !("replaceSync" in CSSStyleSheet.prototype)) {
      return false;
    }

    keyframeSheet = new CSSStyleSheet();
    document.adoptedStyleSheets = [...document.adoptedStyleSheets, keyframeSheet];
  }

  const slot = 100 / count;
  const fade = Math.min(4, slot / 4);
  const shift = "var(--tittli-announcement-shift, 28px)";

  keyframeSheet.insertRule(
    `@keyframes ${keyframeName(count)} {
      0% { opacity: 0; translate: ${shift} 0; }
      ${fade.toFixed(3)}% { opacity: 1; translate: 0 0; }
      ${(slot - fade).toFixed(3)}% { opacity: 1; translate: 0 0; }
      ${slot.toFixed(3)}% { opacity: 0; translate: calc(${shift} * -1) 0; }
      100% { opacity: 0; translate: calc(${shift} * -1) 0; }
    }`,
    keyframeSheet.cssRules.length,
  );

  keyframeCounts.add(count);
  return true;
}

class TittliTicker extends HTMLElement {

  connectedCallback() {
    const items = this.querySelectorAll(".tittli-announcement__item");

    /* A single message is not a rotation: leave the static markup alone and
     * keep the pause control hidden, since there is nothing to pause. */
    if (items.length < 2 || !ensureKeyframes(items.length)) {
      return;
    }

    items.forEach((item, index) => {
      item.style.setProperty("--tittli-announcement-index", index);
    });

    this.style.setProperty("--tittli-announcement-count", items.length);
    this.style.setProperty("--tittli-announcement-keyframes", keyframeName(items.length));
    this.classList.add("is-ready");

  }

  /*
   * There is no pause button any more; it was removed from the bar because it
   * was the only thing in it that looked like a control.
   *
   * What is left is the hover and focus-within pausing in CSS, and
   * `.is-paused` on this element, which still stops the rotation if anything
   * sets it. That is weaker than WCAG 2.2.2 asks for: the bar rotates by
   * itself for longer than five seconds and there is now no mechanism a
   * touch or keyboard user can reach to stop it. Putting the control back
   * visually hidden until it takes focus would satisfy the criterion without
   * showing anything.
   */
}

if (!customElements.get("tittli-ticker")) {
  customElements.define("tittli-ticker", TittliTicker);
}

/* ===================== tittli-slider.js ===================== */
/*
 * <tittli-slider> — the shared horizontal product/reel rail.
 *
 * Scrolling is native (CSS scroll-snap), so swipe, momentum and keyboard
 * scrolling all come from the browser. This element only adds the arrow
 * buttons and keeps them honest:
 * - both hidden when the rail doesn't overflow at all, because an arrow that
 *   cannot move anything is a lie about the content;
 * - at either end the arrow that has nowhere to go is DISABLED rather than
 *   hidden. Hiding it moved the other one and left a rail that looked like it
 *   had one control at the start, two in the middle and one at the end — the
 *   buttons drew more attention by appearing and disappearing than by sitting
 *   still. A dimmed arrow also says "this is as far as it goes", where a
 *   missing one says nothing at all.
 *
 * It also builds the page dots. They are generated here rather than in Liquid
 * because how many there are depends on how much the rail actually overflows,
 * which is a layout question the template cannot answer — at one width a rail
 * may be three pages, at another it may not scroll at all. A rail that does
 * not overflow gets no dots, so the dots are a reliable signal that a section
 * can be swiped.
 *
 * Overflow is tracked with a ResizeObserver rather than a window resize
 * listener, so it also updates when fonts or images change the rail's
 * width, and when a hidden tab panel is shown.
 */

class TittliSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.prev = this.querySelector('[data-slider-prev]');
    this.next = this.querySelector('[data-slider-next]');
    if (!this.track) return;

    this.prev?.addEventListener('click', () => this.page(-1));
    this.next?.addEventListener('click', () => this.page(1));

    let ticking = false;
    this.onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        this.update();
        ticking = false;
      });
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.dots = document.createElement('div');
    this.dots.className = 'tittli-slider__dots';
    this.dots.hidden = true;
    this.dots.addEventListener('click', (event) => {
      const dot = event.target.closest('[data-slider-dot]');
      if (dot) this.goTo(Number(dot.dataset.sliderDot));
    });
    this.append(this.dots);
    this.pageCount = 0;

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
    }

    this.update();
  }

  /* Pages are whole rail-widths. Dots exist to say "there is more this way"
   * and to jump, so counting them in screenfuls matches what a swipe does —
   * counting cards would give twelve dots for a rail showing four at a time. */
  pages() {
    const { scrollWidth, clientWidth } = this.track;
    if (!clientWidth) return 0;
    return Math.ceil((scrollWidth - 4) / clientWidth);
  }

  goTo(index) {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: index * this.track.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
  }

  renderDots(count) {
    if (count === this.pageCount) return;
    this.pageCount = count;

    if (count < 2) {
      this.dots.hidden = true;
      this.dots.replaceChildren();
      return;
    }

    const label = this.getAttribute('data-dots-label') || 'Slide';
    this.dots.replaceChildren(
      ...Array.from({ length: count }, (_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'tittli-slider__dot';
        dot.dataset.sliderDot = i;
        dot.setAttribute('aria-label', `${label} ${i + 1} / ${count}`);
        return dot;
      }),
    );
    this.dots.hidden = false;
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  /* One "page" is 80% of the visible rail: enough to reveal new cards while
   * keeping one partly in view as a sense of place. Snap settles the rest. */
  page(direction) {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: direction * this.track.clientWidth * 0.8, behavior: reduced ? 'auto' : 'smooth' });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const overflows = scrollWidth > clientWidth + 4;
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    const position = Math.abs(scrollLeft);
    const atStart = position <= 4;
    const atEnd = position >= scrollWidth - clientWidth - 4;

    /* `disabled` rather than `aria-disabled`: there is nothing here a screen
     * reader user needs to reach at the end of a rail, and a real disabled
     * button is skipped by the tab order for free. */
    const setArrow = (button, spent) => {
      if (!button) return;
      button.hidden = !overflows;
      button.disabled = spent;
    };
    setArrow(this.prev, rtl ? atEnd : atStart);
    setArrow(this.next, rtl ? atStart : atEnd);

    this.renderDots(overflows ? this.pages() : 0);

    if (!this.dots.hidden) {
      /* Clamped because the last page is usually a partial one: without this
       * the final dot never activates when the rail stops short of a whole
       * screen. */
      const active = Math.min(Math.round(position / clientWidth), this.pageCount - 1);
      [...this.dots.children].forEach((dot, i) => {
        dot.classList.toggle('is-active', i === active);
        dot.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
    }
  }
}

if (!customElements.get('tittli-slider')) {
  customElements.define('tittli-slider', TittliSlider);
}

/* ===================== tittli-quick-add.js ===================== */
/*
 * Tittli quick add — the size/colour sheet behind every card's ADD TO CART.
 *
 * Cards render a plain link to the product page with `data-quick-add`. This
 * module intercepts those clicks (one delegated listener for the whole page,
 * so cards added later — tab panels, load-more — work without re-binding),
 * fetches `/products/{handle}.js`, and renders the product's options into
 * the shared <dialog> from snippets/tittli-quick-add.liquid.
 *
 * Adding goes through Shopify's AJAX cart with `sections: cart-icon-bubble`,
 * so the header badge is re-rendered by the same section Dawn's own cart code
 * uses. If Dawn's pub/sub is present, a cart-update event is published too,
 * so a cart drawer or notification listening for it stays in sync.
 *
 * Product JSON is cached per handle for the page's lifetime: opening the
 * same card twice costs one request.
 */

const dialog = document.getElementById('TittliQuickAdd');

if (dialog && !dialog.dataset.ready) {
  dialog.dataset.ready = 'true';

  const body = dialog.querySelector('[data-qa-body]');
  const labels = dialog.dataset;
  const cache = new Map();
  let state = null;
  let invoker = null;
  let controller = null;

  const root = window.Shopify?.routes?.root || '/';
  const cartAddUrl = `${window.routes?.cart_add_url || `${root}cart/add`}.js`;

  const escapeHtml = (value) =>
    String(value ?? '').replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char],
    );

  /* Mirrors the cards' Liquid output: shop money format, trailing ".00"
   * dropped, and the store's "Rs." prefix swapped for a real ₹ glyph. */
  const formatMoney = (cents) => {
    const value = cents / 100;
    const inr = (digits) => value.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits });
    const tokens = {
      amount: inr(2),
      amount_no_decimals: inr(0),
      amount_with_comma_separator: inr(2).replace('.', ','),
      amount_no_decimals_with_comma_separator: inr(0),
    };
    const format = labels.moneyFormat || '₹{{amount}}';
    return format
      .replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => tokens[key] ?? tokens.amount)
      .replace(/[.,]00(?!\d)/, '')
      .replace(/Rs\.\s?/, '₹')
      .replace(/₹\s+/, '₹');
  };

  const imageUrl = (src, width) => {
    if (!src) return '';
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', width);
    return url.toString();
  };

  /* ---------- data ---------- */

  const loadProduct = async (handle) => {
    if (cache.has(handle)) return cache.get(handle);
    controller?.abort();
    controller = new AbortController();
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { signal: controller.signal });
    if (!response.ok) throw new Error(`Product ${handle}: ${response.status}`);
    const product = await response.json();
    cache.set(handle, product);
    return product;
  };

  /* The variant matching every selected option, or null while incomplete. */
  const currentVariant = () =>
    state.product.variants.find((variant) => variant.options.every((value, i) => value === state.selected[i])) ?? null;

  /* Whether picking `value` for option `index` — keeping every other
   * selection — lands on a variant that can actually be bought. */
  const isAvailable = (index, value) =>
    state.product.variants.some(
      (variant) =>
        variant.available &&
        variant.options[index] === value &&
        variant.options.every((v, i) => i === index || state.selected[i] === null || v === state.selected[i]),
    );

  /* ---------- rendering ---------- */

  const renderPrices = (variant) => {
    const source = variant ?? state.product;
    const price = variant ? variant.price : state.product.price;
    const compare = variant ? variant.compare_at_price : state.product.compare_at_price;
    let html = `<span class="tittli-qa__price">${formatMoney(price)}</span>`;
    if (compare && compare > price) {
      const off = Math.round(((compare - price) * 100) / compare);
      html += `<s class="tittli-card__compare">${formatMoney(compare)}</s>`;
      html += `<span class="tittli-card__off">${off}% OFF</span>`;
    }
    return source ? html : '';
  };

  const render = () => {
    const { product } = state;
    const variant = currentVariant();
    const image = variant?.featured_image?.src || product.featured_image;

    const options = product.options
      .map((option, index) => {
        const name = typeof option === 'string' ? option : option.name;
        const values = typeof option === 'string' ? [...new Set(product.variants.map((v) => v.options[index]))] : option.values;
        const selectedValue = state.selected[index];
        const chips = values
          .map((value) => {
            const available = isAvailable(index, value);
            return `<button type="button" class="tittli-qa__chip" data-qa-option="${index}" data-qa-value="${escapeHtml(value)}"
              aria-pressed="${value === selectedValue}" ${available ? '' : 'aria-disabled="true"'}>${escapeHtml(value)}</button>`;
          })
          .join('');
        return `<fieldset class="tittli-qa__option">
            <legend class="tittli-qa__legend">${escapeHtml(name)}${selectedValue ? ` <span>· ${escapeHtml(selectedValue)}</span>` : ''}</legend>
            <div class="tittli-qa__chips">${chips}</div>
          </fieldset>`;
      })
      .join('');

    let buttonLabel = labels.labelAdd;
    let disabled = false;
    if (!variant) {
      buttonLabel = labels.labelSelect;
      disabled = true;
    } else if (!variant.available) {
      buttonLabel = labels.labelSoldOut;
      disabled = true;
    }

    body.innerHTML = `
      <div class="tittli-qa__head">
        <div class="tittli-qa__thumb">${image ? `<img src="${imageUrl(image, 200)}" alt="" width="72" height="96">` : ''}</div>
        <div>
          <h2 class="tittli-qa__title" id="TittliQuickAddTitle">${escapeHtml(product.title)}</h2>
          <div class="tittli-qa__prices">${renderPrices(variant)}</div>
        </div>
      </div>
      ${options}
      <div class="tittli-qa__actions">
        <button type="button" class="tittli-qa__add" data-qa-add ${disabled ? 'disabled' : ''}>${escapeHtml(buttonLabel)}</button>
        <a class="tittli-qa__link" href="${escapeHtml(variant?.url || product.url)}">${escapeHtml(labels.labelDetails)}</a>
      </div>
      <p class="tittli-qa__status" data-qa-status role="status"></p>`;
  };

  const renderDone = () => {
    body.innerHTML = `
      <p class="tittli-qa__done" id="TittliQuickAddTitle">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>
        ${escapeHtml(labels.labelAdded)}
      </p>
      <div class="tittli-qa__actions">
        <a class="tittli-qa__add" href="${escapeHtml(labels.cartUrl)}">${escapeHtml(labels.labelViewCart)}</a>
        <button type="button" class="tittli-qa__secondary" data-qa-close>${escapeHtml(labels.labelContinue)}</button>
      </div>`;
    body.querySelector('.tittli-qa__add')?.focus();
  };

  /* ---------- actions ---------- */

  const open = async (handle, trigger) => {
    invoker = trigger;
    body.innerHTML = `<p class="tittli-qa__loading">${escapeHtml(labels.labelLoading)}</p>`;
    if (!dialog.open) dialog.showModal();

    try {
      const product = await loadProduct(handle);
      // Start from the first variant that can actually be bought, so the
      // sheet opens ready to add rather than on a sold-out combination.
      const start = product.variants.find((v) => v.available) ?? product.variants[0];
      state = { product, selected: start ? [...start.options] : product.options.map(() => null) };
      // A size is a real decision on a garment; only preselect it when
      // there is no choice to make.
      product.options.forEach((option, i) => {
        const name = (typeof option === 'string' ? option : option.name).toLowerCase();
        const values = new Set(product.variants.map((v) => v.options[i]));
        if (name.includes('size') && values.size > 1) state.selected[i] = null;
      });
      render();
      body.querySelector('.tittli-qa__chip:not([aria-disabled="true"])')?.focus();
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Can't show the sheet — fall back to the product page, which is
      // exactly where the link was going anyway.
      window.location.href = trigger.href;
    }
  };

  const updateCartBubble = (sections) => {
    const html = sections?.['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (!html || !bubble) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    bubble.innerHTML = (parsed.querySelector('.shopify-section') ?? parsed.body).innerHTML;
  };

  const add = async () => {
    const variant = currentVariant();
    if (!variant?.available) return;

    const button = body.querySelector('[data-qa-add]');
    const status = body.querySelector('[data-qa-status]');
    button.disabled = true;
    button.textContent = labels.labelAdding;
    status.textContent = '';
    status.classList.remove('tittli-qa__status--error');

    // With the cart drawer on, ask for the drawer's own sections too and hand
    // the response to Dawn's cart-drawer.js — exactly what product-form.js
    // does — so the drawer opens on the new line instead of this sheet.
    const drawer = document.querySelector('cart-drawer');
    const useDrawer = typeof drawer?.renderContents === 'function' && typeof drawer?.getSectionsToRender === 'function';
    const sections = useDrawer ? drawer.getSectionsToRender().map((section) => section.id) : ['cart-icon-bubble'];

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || labels.labelError);

      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'tittli-quick-add', productVariantId: variant.id, cartData: data });
      }

      if (useDrawer) {
        invoker = null; // focus moves into the drawer, not back to the card
        dialog.close();
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
        return;
      }

      updateCartBubble(data.sections);
      renderDone();
    } catch (error) {
      button.disabled = false;
      button.textContent = labels.labelAdd;
      status.textContent = error.message || labels.labelError;
      status.classList.add('tittli-qa__status--error');
    }
  };

  /* ---------- events ---------- */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-quick-add]');
    if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
    event.preventDefault();
    open(trigger.dataset.quickAdd, trigger);
  });

  dialog.addEventListener('click', (event) => {
    // Clicks on the dialog element itself are backdrop clicks.
    if (event.target === dialog || event.target.closest('[data-qa-close]')) {
      dialog.close();
      return;
    }

    const chip = event.target.closest('[data-qa-option]');
    if (chip && chip.getAttribute('aria-disabled') !== 'true') {
      const index = Number(chip.dataset.qaOption);
      state.selected[index] = chip.dataset.qaValue;
      render();
      body.querySelector(`[data-qa-option="${index}"][aria-pressed="true"]`)?.focus();
      return;
    }

    if (event.target.closest('[data-qa-add]')) add();
  });

  dialog.addEventListener('close', () => {
    controller?.abort();
    invoker?.focus();
    invoker = null;
  });
}
